import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ConfigurationModel } from '@tink/config/src/tinkLinkConfig';
import { ProductType } from '@tink/core/src/types';
import { MarketCode } from '@tink/config/src/markets';
import { applicationParameters } from '../../../utilities/application-parameters';
import { PageView } from '../../../models/analytics';
import {
  hasHiddenTermsAndScopeMessages,
  hasSkipConsentScreen,
  shouldSkipUserConfirmation,
} from '../../../models/configuration';
import { CredentialsEntity } from '../../../models/credentials';
import { handoffFlowAvailable, ProviderEntity } from '../../../models/provider';
import { AppState, ConnectedPropsWithoutDispatch } from '../../../stores/redux-store';
import { useAnalytics } from '../../../core/Analytics/AnalyticsProvider';
import { useConfiguration } from '../../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { RefreshableItem } from '../../../models/refreshableItem';
import { useLegal } from '../../ApplicationProviders/LegalProvider/LegalProvider';
import {
  LoadingOverlay as LoadingOverlayImport,
  LoadingOverlayProps,
} from '../../../core/screens/LoadingOverlay/LoadingOverlay';
import { sendSubmitCredentialsEvents } from '../molecules/SubmitCredentialsScreen/LoginMachineView';
import { useProduct } from '../../ApplicationProviders/ProductProvider/ProductProvider';
import { SessionModel } from '../../../models/session';
import { BackButton } from '../../organisms/Header/HeaderMolecule';
import {
  SubmitCredentialsPayload,
  useSubmitCredentials as useSubmitCredentialsImport,
} from './hooks/useSubmitCredentials/useSubmitCredentials';
import {
  SubmitCredentialsController as SubmitCredentialsControllerAsImport,
  SubmitCredentialsControllerOwnProps,
} from './SubmitCredentialsController';
import { ProviderInputValidator } from './molecules/ProviderInputs/ProviderInputValidator';

export type ShouldAutoSubmitCredentialsPayload = {
  provider: ProviderEntity;
  configuration: ConfigurationModel;
  userHasReadDisclaimer: boolean;
  isClientVerified: boolean;
  productType: ProductType | undefined;
  market: MarketCode | undefined;
  session?: SessionModel;
  credentials?: CredentialsEntity;
  userId?: string;
  enableAutoSubmitCredentials?: boolean;
};

export const shouldAutoSubmitCredentials = ({
  provider,
  configuration,
  productType,
  credentials,
  session,
  userId,
  userHasReadDisclaimer,
  isClientVerified,
  market,
  enableAutoSubmitCredentials = true,
}: ShouldAutoSubmitCredentialsPayload): boolean => {
  const haveExistingCredentials = Boolean(credentials);
  const isHandoffFlowAvailable = handoffFlowAvailable(provider, configuration, userId);
  const providerHasNoFields = provider.fields && provider.fields.length === 0;
  const hiddenTermsAndScopes = hasHiddenTermsAndScopeMessages(configuration);

  let skipSSNScreen = false;

  if (
    session?.personalIdentifier &&
    hasSkipConsentScreen(configuration) &&
    market === MarketCode.SE
  ) {
    skipSSNScreen = Boolean(
      provider.fields.filter(
        (field) =>
          new ProviderInputValidator(field).validate(session.personalIdentifier!).onType
            ?.isValid === true
      ).length
    );
  }

  const isAutoSubmitEligible =
    !haveExistingCredentials &&
    !isHandoffFlowAvailable &&
    (providerHasNoFields || skipSSNScreen) &&
    enableAutoSubmitCredentials;
  const isAutoSubmitLegal = hiddenTermsAndScopes || (userHasReadDisclaimer && isClientVerified);

  if (productType === ProductType.CREDENTIALS || productType === ProductType.TRANSACTIONS) {
    return (
      isAutoSubmitEligible &&
      isAutoSubmitLegal &&
      (shouldSkipUserConfirmation(configuration) || (userHasReadDisclaimer && isClientVerified))
    );
  }

  return isAutoSubmitEligible && isAutoSubmitLegal;
};

enum AutoSubmitStates {
  INITIAL, // This is on mount, we show spinner and try to auto submit
  SUBMIT_STARTED, // We made the auto submit and wait for resulting credentials
  SUBMIT_FINISHED, // We did the auto submit and now have credentials OR we couldn't auto submit
}

export type SubmitCredentialsProps = {
  provider: ProviderEntity;
  refreshableItems: RefreshableItem[];
  triggerRefresh?: boolean;
  enableAutoSubmitCredentials?: boolean;
} & BackButton;

export type SubmitCredentialsWrapperProps = PropsFromRedux &
  SubmitCredentialsProps & {
    credentials?: CredentialsEntity;
    useSubmitCredentials?: typeof useSubmitCredentialsImport;
    SubmitCredentialsController?: React.ComponentType<SubmitCredentialsControllerOwnProps>;
    shouldAutoSubmitCredentialsFn?: typeof shouldAutoSubmitCredentials;
    LoadingOverlay?: React.ComponentType<LoadingOverlayProps>;
  };

export const SubmitCredentialsWrapperPresenter: React.FC<SubmitCredentialsWrapperProps> = ({
  provider,
  credentials,
  session,
  onBack,
  triggerRefresh,
  enableAutoSubmitCredentials = true,
  refreshableItems,
  useSubmitCredentials = useSubmitCredentialsImport,
  SubmitCredentialsController = SubmitCredentialsControllerAsImport,
  userId,
  shouldAutoSubmitCredentialsFn = shouldAutoSubmitCredentials,
  isClientVerified,
  LoadingOverlay = LoadingOverlayImport,
}) => {
  const { productType } = useProduct();
  const { data: configurationData } = useConfiguration();
  const { tinklinkAnalytics } = useAnalytics();
  const { submitCredentials } = useSubmitCredentials();
  const { hasUserReadLegalDisclaimer } = useLegal();
  const [autoSubmitState, setAutoSubmitState] = useState<AutoSubmitStates>(
    AutoSubmitStates.INITIAL
  );
  const market = applicationParameters.get('market');

  const onSubmitCredentials = useCallback(
    ({ fields }: SubmitCredentialsPayload) => {
      sendSubmitCredentialsEvents(tinklinkAnalytics, provider);

      submitCredentials({
        request: { fields },
        credentials,
        refreshableItems,
        triggerRefresh,
      });
    },
    [tinklinkAnalytics, provider, submitCredentials, credentials, refreshableItems, triggerRefresh]
  );

  const autoSubmitCredentials = shouldAutoSubmitCredentialsFn({
    provider,
    configuration: configurationData,
    productType,
    credentials,
    session,
    userId,
    userHasReadDisclaimer: hasUserReadLegalDisclaimer(PageView.SUBMIT_CREDENTIALS_SCREEN),
    isClientVerified,
    market,
    enableAutoSubmitCredentials,
  });

  useEffect(() => {
    if (autoSubmitCredentials && autoSubmitState === AutoSubmitStates.INITIAL) {
      setAutoSubmitState(AutoSubmitStates.SUBMIT_STARTED);

      onSubmitCredentials({
        fields: session?.personalIdentifier ? { username: session.personalIdentifier } : {},
      });
    }
  }, [autoSubmitCredentials, autoSubmitState, onSubmitCredentials, session]);

  useEffect(() => {
    if (credentials && autoSubmitState === AutoSubmitStates.SUBMIT_STARTED) {
      setAutoSubmitState(AutoSubmitStates.SUBMIT_FINISHED);
    }
  }, [autoSubmitState, credentials]);

  if (
    autoSubmitCredentials &&
    !credentials &&
    (autoSubmitState === AutoSubmitStates.INITIAL ||
      autoSubmitState === AutoSubmitStates.SUBMIT_STARTED)
  ) {
    return <LoadingOverlay />;
  }

  return (
    <SubmitCredentialsController
      provider={provider}
      onBack={onBack}
      refreshableItems={refreshableItems}
      onSubmitCredentials={onSubmitCredentials}
    />
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    credentials: state.credentials.data,
    userCredentials: state.userCredentials.data,
    userId: state.user.user?.id,
    isClientVerified: Boolean(state.oauth.description?.verified),
    session: state.session.data,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedPropsWithoutDispatch<typeof connector>;

export const SubmitCredentialsWrapper = connector(SubmitCredentialsWrapperPresenter);
